import { useSettings } from '@capsicum/common';
import { useNCWBalance } from '@capsicum/socios-core';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { v2MigrationInitFlowStatusAtom } from '../data-access/atoms';
import {
	type UseV2FTMigrationInitFlowProps,
	type V2FTMigrationScreenProps,
	type V2FTMigrationScreens
} from '../models/v2FTMigrationInitFlow';

import { useMigrateableFanTokens } from './useMigrateableFanTokens';
import { useV2MigrationTxHashSync } from './useV2MigrationTxHashSync';

export type UseSharedV2FTMigrationInitFlowProps = Omit<UseV2FTMigrationInitFlowProps, 'fallback'> & {
	enabled: boolean;
};

export const useV2FTMigrationInitFlow = ({
	enabled,
	isReactive,
	next,
	screens
}: UseSharedV2FTMigrationInitFlowProps) => {
	const { isPending: isNCWBalanceLoading, ncwBalance } = useNCWBalance();
	const [screenName, setScreen] = useState<V2FTMigrationScreens>('MODAL');
	const setV2MigrationInitFlowStatus = useSetAtom(v2MigrationInitFlowStatusAtom);
	const { isLoading: isMigrateableFanTokensLoading, migrateableTokens } = useMigrateableFanTokens();

	const {
		areSettingsLoading,
		settings: { fanTokenV2MigrationEnabled }
	} = useSettings();

	const isEnabled = enabled && isReactive && !areSettingsLoading && fanTokenV2MigrationEnabled;

	const { isLoading: isV2MigrationTxHashSyncLoading } = useV2MigrationTxHashSync({
		enabled: isEnabled && screenName === 'MODAL'
	});

	const isLoading = isNCWBalanceLoading || isMigrateableFanTokensLoading || isV2MigrationTxHashSyncLoading;
	const isVisible = isEnabled && !isLoading && migrateableTokens.length > 0 && ncwBalance?.ongoingMigrations === 0;

	useEffect(() => {
		if (!isReactive) next();
	}, [next, isReactive]);

	useEffect(() => {
		setV2MigrationInitFlowStatus(isLoading ? 'loading' : isVisible ? 'visible' : 'hidden');
	}, [isLoading, isVisible, setV2MigrationInitFlowStatus]);

	const screenProps = useMemo<V2FTMigrationScreenProps>(
		() => ({ changeScreen: setScreen, tokens: migrateableTokens }),
		[migrateableTokens]
	);

	const screen = useMemo(() => {
		const { FAQ, MODAL, UPGRADE } = screens;

		return match({
			isVisible,
			screenName
		})
			.with({ isVisible: false }, () => null)
			.with({ screenName: 'FAQ' }, () => <FAQ {...screenProps} />)
			.with({ screenName: 'MODAL' }, () => <MODAL {...screenProps} />)
			.with({ screenName: 'UPGRADE' }, () => <UPGRADE {...screenProps} />)
			.otherwise(() => null);
	}, [screens, isVisible, screenName, screenProps]);

	return {
		name: screenName,
		screen
	};
};
